// File: frontend/src/views/public/About.js

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Shield, Eye, Users, Zap, ArrowRight, Check, X } from 'lucide-react';
import ScrollAnimation from '../../components/common/ScrollAnimation';
import PageContainer from '../../components/layout/PageContainer';
import AuthModal from '../../components/auth/AuthModal';
import styles from './About.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';
import { ASSETS_BASE_URL } from '../../config/constants';

const About = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const seo = SEO_CONFIG.about;

  const VALUES = [
    {
      icon: Eye,
      title: 'Transparency First',
      description: 'No jargon, no fine print surprises. We translate insurance into plain English so you actually understand what you\'re paying for.'
    },
    {
      icon: Shield,
      title: 'Your Data, Your Control',
      description: 'We never sell your data. We don\'t work for insurers. We work for you. Your policies are yours, and you can delete them anytime.'
    },
    {
      icon: Users,
      title: 'Built for Real People',
      description: 'Insurance is confusing by design. We\'re fixing that. Our AI reads policies so you don\'t have to, and answers your questions in seconds.'
    },
    {
      icon: Zap,
      title: 'Always Improving',
      description: 'We\'re constantly adding features based on your feedback. From claims assistance to rate comparison, we build what you actually need.'
    }
  ];

  const DIFFERENCES = [
    {
      traditional: 'Sell you insurance',
      allegory: 'Help you understand what you already have'
    },
    {
      traditional: 'Make money from commissions',
      allegory: 'Make money from subscriptions'
    },
    {
      traditional: 'Complicated policy documents',
      allegory: 'AI translates everything into plain English'
    },
    {
      traditional: 'You call them when there\'s a problem',
      allegory: 'We alert you before problems happen'
    },
    {
      traditional: 'Hidden exclusions and gaps',
      allegory: 'We find gaps and tell you exactly what\'s missing'
    }
  ];

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <SEO 
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Hero Section */}
          <section className={styles.hero}>
            <div className={styles.heroContent}>
              <ScrollAnimation>
                <h1 className={styles.heroTitle}>
                  We're Not an Insurance Company.
                  <br />
                  <span className={styles.heroTitleHighlight}>We're Your Advocate.</span>
                </h1>
                <p className={styles.heroSubtitle}>
                  Allegory is a digital insurance wallet that helps you understand, organize, and optimize your policies — so you never overpay, miss a renewal, or get caught off guard by coverage gaps.
                </p>
              </ScrollAnimation>
            </div>
          </section>

          {/* Our Story Section */}
          <section className={styles.section}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={styles.imageContainer}>
                  <img
                    src={`${ASSETS_BASE_URL}/images/about-story.jpg`}
                    alt="Person reviewing insurance documents"
                    className={styles.image}
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.content}>
                  <span className={styles.label}>Our Story</span>
                  <h2 className={styles.sectionTitle}>
                    Insurance Is Broken.
                    <br />We're Fixing It.
                  </h2>
                  <div className={styles.textBlock}>
                    <p>
                      Most people have no idea what their insurance actually covers. They pay premiums every month, but when it's time to file a claim, they discover exclusions they never knew existed.
                    </p>
                    <p>
                      Insurance companies have experts. Lawyers. Adjusters. Teams of people whose job is to know every loophole in your policy.
                    </p>
                    <p>
                      <strong>You deserve experts too.</strong>
                    </p>
                    <p>
                      That's why we built Allegory — a digital wallet where you upload your policies, and our AI reads every line, finds every gap, and answers every question. No jargon. No runaround. Just straight answers about what you're actually covered for.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* Mission Section */}
          <section className={styles.missionSection}>
            <div className={styles.sectionGrid}>
              <ScrollAnimation>
                <div className={styles.content}>
                  <span className={styles.label}>Our Mission</span>
                  <h2 className={styles.sectionTitle}>
                    Empowering Consumers with Transparency and Control
                  </h2>
                  <div className={styles.textBlock}>
                    <p>
                      We believe insurance should be simple, transparent, and fair. You shouldn't need a law degree to understand what you're paying for.
                    </p>
                    <p>
                      Our mission is to give every consumer the tools and insights they need to make informed decisions about their insurance — whether that's finding coverage gaps, comparing rates, or filing claims without the headache.
                    </p>
                    <p>
                      We're not here to sell you insurance. We're here to make sure you're getting what you paid for.
                    </p>
                  </div>
                </div>
              </ScrollAnimation>

              <ScrollAnimation delay={200}>
                <div className={styles.imageContainer}>
                  <img
                    src={`${ASSETS_BASE_URL}/images/about-mission.jpg`}
                    alt="Team collaborating"
                    className={styles.image}
                    loading="lazy"
                    width={800}
                    height={600}
                  />
                </div>
              </ScrollAnimation>
            </div>
          </section>

          {/* How We're Different */}
          <section className={styles.section}>
            <div className={styles.comparisonContainer}>
              <ScrollAnimation>
                <div className={styles.comparisonHeader}>
                  <span className={styles.label}>How We're Different</span>
                  <h2 className={styles.sectionTitle}>
                    Traditional Insurance vs. Allegory
                  </h2>
                  <p className={styles.comparisonSubtitle}>
                    We're not trying to replace your insurance company. We're here to make sure they're doing their job.
                  </p>
                </div>
              </ScrollAnimation>

              <div className={styles.comparisonTable}>
                {DIFFERENCES.map((diff, index) => (
                  <ScrollAnimation key={index} delay={index * 50}>
                    <div className={styles.comparisonRow}>
                      <div className={styles.comparisonCell}>
                        <X size={20} strokeWidth={2.5} className={styles.crossIcon} />
                        <span>{diff.traditional}</span>
                      </div>
                      <div className={styles.comparisonCell}>
                        <Check size={20} strokeWidth={2.5} className={styles.checkIcon} />
                        <span>{diff.allegory}</span>
                      </div>
                    </div>
                  </ScrollAnimation>
                ))}
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className={styles.valuesSection}>
            <div className={styles.valuesContainer}>
              <ScrollAnimation>
                <div className={styles.valuesHeader}>
                  <span className={styles.label}>Our Values</span>
                  <h2 className={styles.sectionTitle}>
                    What We Stand For
                  </h2>
                </div>
              </ScrollAnimation>

              <div className={styles.valuesGrid}>
                {VALUES.map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <ScrollAnimation key={index} delay={index * 80}>
                      <div className={styles.valueCard}>
                        <div className={styles.valueIcon}>
                          <IconComponent size={28} strokeWidth={2} />
                        </div>
                        <h3 className={styles.valueTitle}>{value.title}</h3>
                        <p className={styles.valueDescription}>{value.description}</p>
                      </div>
                    </ScrollAnimation>
                  );
                })}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className={styles.ctaSection}>
            <div className={styles.ctaBg} />
            <ScrollAnimation>
              <div className={styles.ctaContent}>
                <h2 className={styles.ctaTitle}>
                  Ready to Take Control of Your Insurance?
                </h2>
                <p className={styles.ctaSubtitle}>
                  Join 10,000+ Canadians who already know exactly what their policies cover.
                </p>
                <button className={styles.ctaButton} onClick={handleCTA}>
                  {isAuthenticated ? 'Go to Dashboard' : 'Get Started Free'}
                  <ArrowRight size={20} strokeWidth={2.5} />
                </button>
              </div>
            </ScrollAnimation>
          </section>
        </div>

      </PageContainer>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialType="sign_up"
      />
    </>
  );
};

export default About;